import React, { useState, useEffect } from "react";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import CustomButton from "components/common/Button";
import Text from "components/common/Text";

import "./FeatureDialog.scss";

/**
 * FeatureDialog — create or edit a feature in the RBAC catalog.
 *
 * ID derivation: Feature_ID is synthesised client-side as "feat_" + Key (lowercase).
 * The backend create endpoint expects the full Feature_ID field; for updates it is
 * addressed by the existing featureId path param so Feature_ID is omitted.
 *
 * props: { open, mode: "create"|"edit", feature, onClose, onSubmit }
 * onSubmit receives: { mode, featureId, Key, Name, Description, Display_Order, Feature_ID }
 */
export default function FeatureDialog({ open, mode, feature, onClose, onSubmit }) {
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [displayOrder, setDisplayOrder] = useState("");

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && feature) {
      setKey(feature.key || "");
      setName(feature.name || "");
      setDescription(feature.description || "");
      setDisplayOrder(feature.displayOrder != null ? String(feature.displayOrder) : "");
    } else {
      setKey("");
      setName("");
      setDescription("");
      setDisplayOrder("");
    }
  }, [open, mode, feature]);

  const canSubmit = key.trim() !== "" && name.trim() !== "";

  const submit = () => {
    const trimmedKey = key.trim();
    onSubmit({
      mode,
      featureId: feature?.featureId,
      Key: trimmedKey,
      Name: name.trim(),
      Description: description.trim(),
      Display_Order: displayOrder !== "" ? Number(displayOrder) : undefined,
      // Client-side ID derivation: "feat_" + key (mirrors catalog convention)
      Feature_ID: `feat_${trimmedKey}`,
    });
  };

  const dialogContent = (
    <DialogContent>
      <div className="feature-dialog-fields">
        <Text
          name="feature-key"
          label="Key (e.g. billing)"
          value={key}
          disabled={mode === "edit"}
          onChange={(e) => setKey(e.target.value.toLowerCase().replace(/\s+/g, "_"))}
          helperText={
            mode === "create"
              ? 'Lowercase identifier. Feature_ID will be "feat_" + key.'
              : "Key is immutable after creation."
          }
        />
        <Text name="feature-name" label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Text
          name="feature-description"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
        />
        <Text
          name="feature-display-order"
          label="Display order (number)"
          type="number"
          value={displayOrder}
          onChange={(e) => setDisplayOrder(e.target.value)}
        />
      </div>
    </DialogContent>
  );

  const dialogActions = (
    <DialogActions
      action1={<CustomButton label="Cancel" variant="text" onClick={onClose} />}
      action2={<CustomButton label="Save" variant="contained" onClick={submit} isDisabled={!canSubmit} />}
    />
  );

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      size="sm"
      title={<DialogTitle title={mode === "edit" ? `Edit feature "${feature?.name || ""}"` : "Add feature"} />}
      content={dialogContent}
      actions={dialogActions}
    />
  );
}
